// SHA claims and pre-authorisations (docs/plans/phase1-sha.md §8.3, §9.4, §9.5).
import { queryOptions } from '@tanstack/react-query';
import {
  claimDetailSchema,
  claimListSchema,
  preauthListSchema,
  preauthViewSchema,
  prepareClaimResponseSchema,
  visitPreauthGatesSchema,
  type ClaimListView,
  type PreauthRequestBody,
} from '@wodi/shared';

import { api } from './client.ts';

export const claimKeys = {
  all: ['claims'] as const,
  list: (view: ClaimListView) => ['claims', 'list', view] as const,
  detail: (id: string) => ['claims', 'detail', id] as const,
  preauth: ['claims', 'preauth'] as const,
  /** Under the visit's orders key, so refreshing the orders refreshes their approvals. */
  visitGates: (visitId: string) => ['visits', visitId, 'orders', 'preauth-gates'] as const,
};

/** How often the order screens look again while SHA hasn't answered. */
export const PREAUTH_GATES_REFRESH_MS = 15_000;

// --- Pre-authorisation on the order screens (plan §9.3) --------------------------------------

export const visitPreauthGatesQuery = (visitId: string) =>
  queryOptions({
    queryKey: claimKeys.visitGates(visitId),
    queryFn: () =>
      api('GET', `/visits/${visitId}/preauth-gates`, { schema: visitPreauthGatesSchema }),
    // Keep following a request until SHA answers (the server's poller asks SHA).
    refetchInterval: (query) =>
      query.state.data?.items.some((i) => i.state === 'waiting') === true
        ? PREAUTH_GATES_REFRESH_MS
        : false,
  });

export function askPreauth(visitId: string, body: PreauthRequestBody) {
  return api('POST', `/visits/${visitId}/preauth-requests`, { body, schema: preauthViewSchema });
}

export function overridePreauth(requestId: string, reason: string) {
  return api('POST', `/preauth-requests/${requestId}/override`, {
    body: { reason },
    schema: preauthViewSchema,
  });
}

export function checkPreauthStatus(requestId: string) {
  return api('POST', `/preauth-requests/${requestId}/check-status`, {
    schema: preauthViewSchema,
  });
}

export const claimListQuery = (view: ClaimListView) =>
  queryOptions({
    queryKey: claimKeys.list(view),
    queryFn: () => api('GET', `/claims?view=${view}`, { schema: claimListSchema }),
  });

export const claimQuery = (id: string) =>
  queryOptions({
    queryKey: claimKeys.detail(id),
    queryFn: () => api('GET', `/claims/${id}`, { schema: claimDetailSchema }),
  });

export const preauthListQuery = queryOptions({
  queryKey: claimKeys.preauth,
  queryFn: () => api('GET', '/preauth-requests', { schema: preauthListSchema }),
});

export function prepareClaim(visitId: string) {
  return api('POST', `/visits/${visitId}/claims`, { schema: prepareClaimResponseSchema });
}

export function submitClaim(id: string, expectedVersion: number, resubmit: boolean) {
  return api('POST', `/claims/${id}/${resubmit ? 'resubmit' : 'submit'}`, {
    body: { expectedVersion },
    schema: claimDetailSchema,
  });
}

export function assignClaimToMe(id: string) {
  return api('POST', `/claims/${id}/assign`, { body: { userId: 'me' }, schema: claimDetailSchema });
}

export function checkClaimStatus(id: string) {
  return api('POST', `/claims/${id}/check-status`, { schema: claimDetailSchema });
}

export type WriteOffReason = 'sha_final_rejection' | 'past_deadline' | 'not_claimable' | 'other';

export function askWriteOff(
  id: string,
  body: { amountCents: string; reasonCode: WriteOffReason; reasonNote?: string },
) {
  return api('POST', `/claims/${id}/write-offs`, { body, schema: claimDetailSchema });
}

export function decideWriteOff(id: string, decision: 'approve' | 'reject') {
  return api('POST', `/claim-write-offs/${id}/${decision}`, {
    body: {},
    schema: claimDetailSchema,
  });
}
